import {
  AdminDetailGrid,
  AdminHeaderActionButton,
  AdminRecordHeader,
  AdminSectionCard,
} from '@/components/patterns/admin-page'
import {
  CUSTOMER_REVIEW_IGNORED_TOOLTIP,
  CustomerReviewStars,
  CustomerReviewStatusBadge,
} from '@/components/customer-reviews/customer-review-display'
import SaveBar from '@/components/forms/SaveBar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DetailWell, detailPaleIconClass } from '@/components/patterns/detail-page'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { CustomerReviewStatus } from '@/lib/enums/CustomerReviewStatus'
import {
  useMarkActivityFeedReadMutation,
  useMarkActivityFeedUnreadMutation,
} from '@/lib/services/activity-feed.query'
import {
  customerReviewQueries,
  customerReviewQueryKeys,
  useCustomerReview,
  useUpdateCustomerReviewMutation,
} from '@/lib/services/customer-reviews/customer-reviews.query'
import { customerReviewIdSchema } from '@/lib/services/customer-reviews/customer-reviews.schema'
import { cn, getErrorMessage } from '@/lib/utils'
import { useAppForm } from '@/lib/form'
import { useStore } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import {
  EyeOff,
  ExternalLink,
  Mail,
  MailCheck,
  MailOpen,
  MessageSquareText,
  Send,
  ShieldCheck,
  Star,
  UserCheck,
  UserX,
  XCircle,
} from 'lucide-react'
import { useEffect } from 'react'
import { toast } from 'sonner'

const CUSTOMER_REVIEW_ACTIVITY_TYPE = 'customer_review'
const reviewStatusOptions = [
  {
    id: CustomerReviewStatus.Published.Value,
    name: 'Public',
    selectedTone: 'success',
    start: (
      <span className="inline-flex items-center" aria-hidden="true">
        <Send className="size-4" />
      </span>
    ),
  },
  {
    id: CustomerReviewStatus.Hidden.Value,
    name: 'Hidden',
    selectedTone: 'warning',
    start: (
      <span className="inline-flex items-center" aria-hidden="true">
        <EyeOff className="size-4" />
      </span>
    ),
  },
  {
    id: CustomerReviewStatus.Rejected.Value,
    name: 'Ignored',
    selectedTone: 'destructive',
    start: (
      <span className="inline-flex items-center" aria-hidden="true">
        <XCircle className="size-4" />
      </span>
    ),
    tooltip: CUSTOMER_REVIEW_IGNORED_TOOLTIP,
    tooltipClassName: 'max-w-64',
  },
]
const reviewStatusRadioButtonClassNames = {
  button: 'justify-center gap-2.5 px-6 leading-none',
}

export const Route = createFileRoute('/_authenticated/$orgId/customer-reviews/$customerReviewId')({
  component: CustomerReviewRouteComponent,
  params: {
    parse: (params) => customerReviewIdSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'customer-reviews')) throw notFound()
    const review = await context.queryClient.ensureQueryData(
      customerReviewQueries.show({
        organizationId: params.orgId,
        customerReviewId: params.customerReviewId,
      })
    )
    return { review, isReadOnly: !context.ability.can('edit', 'customer-reviews') }
  },
})

function getReviewerIdentity(review) {
  const hasCustomerProfile = Boolean(review?.customer_id)
  const hasVerifiedOrder = Boolean(review?.is_verified_purchase || review?.sales_order_id)

  if (hasVerifiedOrder) {
    return {
      label: 'Verified Purchaser',
      description: hasCustomerProfile
        ? 'Matched to a customer profile and an order. This is the strongest match signal for review moderation.'
        : 'Matched to an order, but no customer profile is attached to this review.',
      badgeVariant: 'success',
      icon: ShieldCheck,
      tone: 'success',
      profileValue: hasCustomerProfile ? null : 'No customer profile attached',
      orderValue: review.sales_order_id ? `Order #${review.sales_order_id}` : 'Order linked',
    }
  }

  if (hasCustomerProfile) {
    return {
      label: 'Customer profile matched',
      description:
        'Matched to an existing customer profile, but no order is attached to this review.',
      badgeVariant: 'info',
      icon: UserCheck,
      tone: 'info',
      profileValue: null,
      orderValue: 'No verified order attached',
    }
  }

  return {
    label: 'Unmatched reviewer',
    description:
      'No customer profile matched this submission. Treat the name and email as customer-entered details until you verify them.',
    badgeVariant: 'warning',
    icon: UserX,
    tone: 'warning',
    profileValue: 'No customer profile matched',
    orderValue: 'No verified order attached',
  }
}

function ReviewDetailItem({ label, children, className }) {
  return (
    <div className={cn('min-w-0 space-y-1.5', className)}>
      <Typography as="dt" variant="eyebrow">
        {label}
      </Typography>
      <dd className="min-w-0 break-words text-sm font-medium leading-6 text-foreground">
        {children}
      </dd>
    </div>
  )
}

function ReviewerIdentityCard({ review, identity, orgId, path }) {
  const submittedName = review.reviewer_name || 'No name provided'
  const orderMatchIsMuted = !review.is_verified_purchase && !review.sales_order_id

  return (
    <AdminSectionCard
      icon={identity.icon}
      iconTone={identity.tone}
      title="Reviewer identity"
      accent={identity.tone}
      contentClassName="space-y-4 p-4"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 space-y-2">
          <Badge variant={identity.badgeVariant}>{identity.label}</Badge>
        </div>
      </div>

      <dl className="space-y-4">
        <ReviewDetailItem label="Customer profile">
          {review.customer_id ? (
            <Can I="view" a="customers" passThrough>
              {(canViewCustomer) =>
                canViewCustomer ? (
                  <Button asChild variant="outline" size="sm" className="h-8 px-2.5">
                    <Link {...path.customers.show(review.customer_id).link}>
                      <UserCheck className="size-4" />
                      View customer profile
                      <ExternalLink className="size-3.5" />
                    </Link>
                  </Button>
                ) : (
                  <span>Profile matched</span>
                )
              }
            </Can>
          ) : (
            <span className="text-muted-foreground">{identity.profileValue}</span>
          )}
        </ReviewDetailItem>
        <ReviewDetailItem label="Order match">
          {review.sales_order_id ? (
            <Can I="view" a="sales-orders" passThrough>
              {(canViewSalesOrder) =>
                canViewSalesOrder ? (
                  <Button asChild variant="outline" size="sm" className="h-8 px-2.5">
                    <Link {...path.salesOrders.show(review.sales_order_id).using({ orgId }).link}>
                      {identity.orderValue}
                      <ExternalLink className="size-3.5" />
                    </Link>
                  </Button>
                ) : (
                  <span>{identity.orderValue}</span>
                )
              }
            </Can>
          ) : (
            <span className={orderMatchIsMuted ? 'text-muted-foreground' : undefined}>
              {identity.orderValue}
            </span>
          )}
        </ReviewDetailItem>
        <ReviewDetailItem label="Submitted name">{submittedName}</ReviewDetailItem>
        <ReviewDetailItem label="Reviewer email">
          {review.reviewer_email || (
            <span className="text-muted-foreground">No email provided</span>
          )}
        </ReviewDetailItem>
      </dl>
    </AdminSectionCard>
  )
}

function mailtoHref(review) {
  const reviewerEmail =
    typeof review.reviewer_email === 'string' ? review.reviewer_email.trim() : ''

  if (!reviewerEmail) {
    return null
  }

  const subject = encodeURIComponent('Re: Your review')
  const body = encodeURIComponent(`Hi ${review.reviewer_name?.split(' ')[0] || 'there'},\n\n`)

  return `mailto:${encodeURIComponent(reviewerEmail)}?subject=${subject}&body=${body}`
}

function getReviewModerationFormValues(review) {
  return {
    status: review.status,
    is_anonymous: Boolean(review.is_anonymous),
  }
}

export function CustomerReviewRouteComponent() {
  const { customerReviewId, orgId } = Route.useParams()
  const { isReadOnly } = Route.useLoaderData()
  const path = usePathHelper()
  const queryClient = useQueryClient()
  const { data: review } = useCustomerReview(customerReviewId, { organizationId: orgId })
  const updateMutation = useUpdateCustomerReviewMutation()
  const markReviewReadMutation = useMarkActivityFeedReadMutation({ organizationId: orgId })
  const markReviewUnreadMutation = useMarkActivityFeedUnreadMutation({ organizationId: orgId })
  const reviewForm = useAppForm({
    defaultValues: getReviewModerationFormValues(review),
    validators: {
      onSubmitAsync: async ({ value }) => {
        try {
          await saveReviewModerationChanges(value)
          toast.success('Review saved')
        } catch (error) {
          toast.error('Review save failed', {
            description: getErrorMessage(error),
          })

          return { form: getErrorMessage(error) }
        }
      },
    },
  })
  const draftReviewSettings = useStore(reviewForm.store, (state) => state.values)
  const submittedAt = review?.created_at
    ? dayjs(review.created_at).format('MMM D, YYYY [at] h:mm A')
    : null
  const draftStatus = draftReviewSettings.status
  const draftIsAnonymous = Boolean(draftReviewSettings.is_anonymous)
  const reviewSavePending = updateMutation.isPending
  const reviewEmailHref = mailtoHref(review)
  const reviewerIdentity = getReviewerIdentity(review)

  async function saveReviewModerationChanges(value) {
    const nextStatus = value.status
    const nextIsAnonymous = Boolean(value.is_anonymous)

    await updateMutation.mutateAsync({
      customerReviewId: review.id,
      status: nextStatus,
      is_anonymous: nextIsAnonymous,
    })

    reviewForm.reset({
      status: nextStatus,
      is_anonymous: nextIsAnonymous,
    })
  }

  useEffect(() => {
    reviewForm.reset({
      status: review.status,
      is_anonymous: Boolean(review.is_anonymous),
    })
  }, [reviewForm, review.id, review.status, review.is_anonymous])

  const resetReviewForm = () => {
    reviewForm.reset(getReviewModerationFormValues(review))
    setTimeout(() => reviewForm.reset(getReviewModerationFormValues(review)))
  }

  const applyCachedReviewReadState = (isRead) => {
    queryClient.setQueriesData({ queryKey: customerReviewQueryKeys.all }, (current) => {
      if (!current) {
        return current
      }

      const patchReview = (candidate) =>
        candidate?.id === review.id
          ? {
              ...candidate,
              is_read: isRead,
              read_at: isRead ? candidate.read_at || new Date().toISOString() : null,
            }
          : candidate

      if (Array.isArray(current.data)) {
        return {
          ...current,
          data: current.data.map(patchReview),
        }
      }

      return patchReview(current)
    })
  }

  const handleMarkReviewRead = () => {
    if (!review?.id) {
      return
    }

    markReviewReadMutation.mutate(
      {
        type: CUSTOMER_REVIEW_ACTIVITY_TYPE,
        sourceId: review.id,
      },
      {
        onSuccess: () => applyCachedReviewReadState(true),
        onError: (error) => {
          toast.error('Failed to mark review as read', {
            description: getErrorMessage(error),
          })
        },
      }
    )
  }

  const handleMarkReviewUnread = async () => {
    if (!review?.id) {
      return
    }

    try {
      await markReviewUnreadMutation.mutateAsync({
        type: CUSTOMER_REVIEW_ACTIVITY_TYPE,
        sourceId: review.id,
      })
      applyCachedReviewReadState(false)
    } catch (error) {
      toast.error('Failed to mark review as unread', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <reviewForm.AppForm>
      <reviewForm.Form className="-mr-4 mb-10">
        <div className="max-w-7xl space-y-4 pr-6 pt-1">
          <AdminRecordHeader
            icon={Star}
            iconTone="amethyst"
            title={review.title || `${review.rating}-star review`}
            subtitle={
              submittedAt
                ? `Submitted by ${review.reviewer_name} on ${submittedAt}`
                : review.reviewer_name
            }
            badges={
              <>
                <CustomerReviewStatusBadge status={draftStatus} />
                {draftIsAnonymous ? <Badge variant="outline">Anonymous author</Badge> : null}
              </>
            }
            actions={
              <>
                {!isReadOnly ? (
                  <Can I="edit" a="customer-reviews">
                    {review?.is_read && !markReviewReadMutation.isPending ? (
                      <AdminHeaderActionButton
                        type="button"
                        variant="outline"
                        disabled={markReviewUnreadMutation.isPending}
                        onClick={handleMarkReviewUnread}
                      >
                        <MailOpen className={cn(detailPaleIconClass, 'text-current')} />
                        {markReviewUnreadMutation.isPending
                          ? 'Marking unread...'
                          : 'Mark as unread'}
                      </AdminHeaderActionButton>
                    ) : !review?.is_read && !markReviewUnreadMutation.isPending ? (
                      <AdminHeaderActionButton
                        type="button"
                        variant="outline"
                        disabled={markReviewReadMutation.isPending}
                        onClick={handleMarkReviewRead}
                      >
                        <MailCheck className={cn(detailPaleIconClass, 'text-current')} />
                        {markReviewReadMutation.isPending ? 'Marking read...' : 'Mark as read'}
                      </AdminHeaderActionButton>
                    ) : null}
                  </Can>
                ) : null}
                {reviewEmailHref ? (
                  <Button asChild variant="outline">
                    <a href={reviewEmailHref}>
                      <Mail className="size-4" />
                      Email
                    </a>
                  </Button>
                ) : null}
              </>
            }
          />

          <AdminDetailGrid
            main={
              <>
                <AdminSectionCard
                  icon={MessageSquareText}
                  iconTone="amethyst"
                  title="Review"
                  accent="amethyst"
                >
                  <DetailWell className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <CustomerReviewStars rating={review.rating} iconClassName="size-5" />
                      <Typography variant="label" tone="muted">
                        {review.rating} out of 5 stars
                      </Typography>
                    </div>
                    <blockquote className="whitespace-pre-wrap text-base leading-7 text-foreground">
                      {review.body || 'No written review.'}
                    </blockquote>
                  </DetailWell>
                </AdminSectionCard>

                {!isReadOnly ? (
                  <Can I="edit" a="customer-reviews">
                    <ReviewModerationCard
                      draftIsAnonymous={draftIsAnonymous}
                      resetReviewForm={resetReviewForm}
                      reviewForm={reviewForm}
                      reviewSavePending={reviewSavePending}
                    />
                  </Can>
                ) : null}
              </>
            }
            aside={
              <>
                <ReviewerIdentityCard
                  review={review}
                  identity={reviewerIdentity}
                  orgId={orgId}
                  path={path}
                />
              </>
            }
          />
        </div>
      </reviewForm.Form>
    </reviewForm.AppForm>
  )
}

function ReviewModerationCard({
  draftIsAnonymous,
  resetReviewForm,
  reviewForm,
  reviewSavePending,
}) {
  return (
    <AdminSectionCard
      icon={EyeOff}
      iconTone="neutral"
      title="Display and moderation"
      description="Choose whether this review can appear publicly on your website."
      accent="neutral"
    >
      <reviewForm.AppField name="is_anonymous">
        {(field) => (
          <field.SwitchInput
            id="review-anonymous-toggle"
            label="Show this review as anonymous"
            checked={draftIsAnonymous}
            disabled={reviewSavePending}
          />
        )}
      </reviewForm.AppField>

      <reviewForm.AppField name="status">
        {(field) => (
          <field.RadioButtonGroup
            appearance="segmented"
            id="review-publish-state"
            label="Review status"
            options={reviewStatusOptions}
            size="sm"
            joined
            selectedVariant="outline"
            unselectedVariant="outline"
            disabled={reviewSavePending}
            classNames={reviewStatusRadioButtonClassNames}
          />
        )}
      </reviewForm.AppField>
      <SaveBar
        mutation={{ isPending: reviewSavePending }}
        onReset={resetReviewForm}
        onSave={() => reviewForm.handleSubmit()}
        submitButtonText="Save review"
        resetButtonText="Discard"
        unsavedChangesText="Unsaved review changes"
      />
    </AdminSectionCard>
  )
}
